import { useEffect, useRef, useState } from 'react';
import * as rive from '@rive-app/canvas';
import { assetService } from '@/services/asset-service';

type RivePreviewProps = {
  /** A local file to be previewed - this would be used for previewing files before upload */
  localFile?: File;
  /** The UUID of the RIVE asset to be previewed. */
  assetId?: string;
  /** Optional alt text for the Rive animation */
  alt?: string;
};

export const RivePreview = ({ 
  localFile, 
  assetId, 
  alt = "Rive animation preview"
}: RivePreviewProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const riveInstanceRef = useRef<any>(null);
  const [status, setStatus] = useState<'loading' | 'error' | 'loaded'>('loading');
  const [message, setMessage] = useState('Loading Rive animation...');
  const [messageType, setMessageType] = useState<'normal' | 'error'>('normal');

  // Initialize when canvas is ready
  useEffect(() => {
    if (!(localFile || assetId) || riveInstanceRef.current) {
      return;
    }

    let fileUrl: string | null = null;
    const isCancelled = { value: false };

    const initializeRive = async () => {
      try {
        setStatus('loading');
        setMessage('Loading Rive animation...');
        setMessageType('normal');
        isCancelled.value = false;

        if (localFile) {
          fileUrl = URL.createObjectURL(localFile);
        } else if (assetId) {
          // For assets, fetch the asset first to get the correct URI
          const asset = await assetService.getAsset(assetId);
          if (asset.data && typeof asset.data === 'object' && 'uri' in asset.data) {
            const uri = asset.data.uri as string;
            if (uri && uri.startsWith('/d/')) {
              fileUrl = uri;
            } else {
              console.error('Invalid URI format from API for Rive asset:', uri);
              throw new Error('Invalid URI format from API');
            }
          } else {
            console.warn('No URI found in asset data for Rive asset:', assetId);
            throw new Error('Could not determine download URL for Rive asset');
          }
        }

        // Wait for canvas to be mounted before proceeding
        const waitForCanvas = (): Promise<void> => {
          return new Promise((resolve) => {
            if (canvasRef.current) {
              resolve();
            } else {
              const checkCanvas = () => {
                if (isCancelled.value) {
                  resolve(); // Stop waiting if cancelled
                  return;
                }
                if (canvasRef.current) {
                  resolve();
                } else {
                  requestAnimationFrame(checkCanvas);
                }
              };
              checkCanvas();
            }
          });
        };

        await waitForCanvas();

        if (fileUrl && canvasRef.current && !isCancelled.value) {
          const newRiveInstance = new rive.Rive({
            canvas: canvasRef.current,
            src: fileUrl,
            autoplay: true,
            // artboard: "TOGETHER",
            useOffscreenRenderer: false,
            onLoad: () => {
              if (!isCancelled.value) {
                // Set layout after the Rive file has loaded
                newRiveInstance.layout = new rive.Layout({
                  fit: rive.Fit.Contain, // Make sure the animation fits within the canvas
                  alignment: rive.Alignment.Center, // Center the animation
                });
                
                setStatus('loaded');
                newRiveInstance.resizeDrawingSurfaceToCanvas();
                
                // Enable interaction with the canvas for mouse events
                if (canvasRef.current) {
                  canvasRef.current.style.touchAction = 'none'; // Disable browser's touch actions for better interaction
                }
              }
            },
            onLoadError: () => {
              if (!isCancelled.value) {
                setStatus('error');
                setMessage('Failed to load Rive file');
                setMessageType('error');
              }
            },
            onPlay: () => {
              // Animation is playing
            },
            onStop: () => {
              // Animation has stopped
            },
            onPause: () => {
              // Animation is paused
            },
            // Listen for state changes in interactive Rive animations
            onStateChange: (stateName) => {
              console.log('Rive state changed:', stateName);
            }
          });

          riveInstanceRef.current = newRiveInstance;
        }
      } catch (err) {
        if (!isCancelled.value) {
          console.error('Error setting up Rive:', err);
          setStatus('error');
          setMessage('Error loading Rive animation');
          setMessageType('error');
        }
      }
    };

    initializeRive();

    return () => {
      isCancelled.value = true;
      if (riveInstanceRef.current) {
        riveInstanceRef.current.cleanup();
        riveInstanceRef.current = null;
      }
      if (fileUrl && localFile) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [localFile, assetId]); // Only re-run when these values change

  // The canvas stays mounted, only the overlay visibility changes based on state
  const showOverlay = status !== 'loaded';

  return (
    <div className="w-full h-full flex items-center justify-center p-2">
      <div className="relative w-full h-full">
        <canvas 
          ref={canvasRef} 
          className="w-full h-full"
          aria-label={alt}
          style={{ visibility: showOverlay ? 'hidden' : 'visible', display: 'block' }}
        />
        
        {/* Overlay for loading/error states */}
        {showOverlay && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted">
            <div className="text-center">
              <p className={`text-sm ${messageType === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}>
                {message}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};